import React, { useCallback, useEffect } from 'react';

import { ActionNavy } from '@randstad-lean-mobile-factory/react-assets/dist/headers';
import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  WithLightTitle,
} from '@randstad-lean-mobile-factory/react-components-core';
import { TextInput, useFormWithZodResolver } from '@randstad-lean-mobile-factory/react-form-fields';
import { z } from 'zod';

import SearchableDropDown from '../../../Components/SearchableDropDown';
import { useCreateRouting } from '../../../Hooks/Routings/useCreateRouting';
import { useFetchAMAgencies } from '../../../Hooks/Routings/useFetchAMAgencies';
import { AppelMedicalEventSource, CreateRoutingInfoBody } from '../../../Services/API';
import { brandCodes } from '../../AdminPage/RoutingsSection/RoutingsSection.types';

import styles from './AddRoutingModal.module.scss';
import { Props } from './AddRoutingModal.types';

const AddRoutingModalComponent = ({ isOpen, setIsOpen, selectedSource }: Props) => {
  const schema = z.object({
    qualification: z
      .string()
      .min(1, 'veuillez renseigner la qualification')
      .max(150, '150 caractères maximum'),
    clientName: z
      .string()
      .min(1, 'veuillez renseigner le nom du client')
      .max(250, '250 caractères maximum'),
    siret: z
      .string()
      .regex(/^\d{3} \d{3} \d{3} \d{3} \d{2}$/, 'ex: XXX XXX XXX XXX XX')
      .max(18, '14 caractères maximum'),
    agencyId: z.string().min(1, 'veuillez sélectionner une agence'),
    brandCode: z.string().min(1, 'veuillez sélectionner une marque'),
    service: z.string().max(150, '150 caractères maximum').optional(),
  });

  const createRoutingMutation = useCreateRouting();
  const { control, reset, setValue, watch, handleSubmit } = useFormWithZodResolver({
    schema,
    defaultValues: {
      siret: undefined,
      qualification: undefined,
      clientName: undefined,
      agencyId: undefined,
      brandCode: undefined,
      service: undefined,
    },
  });
  const siret = watch('siret');

  const handleClose = () => {
    reset({});

    setIsOpen(false);
  };

  const formatSiret = useCallback((value?: string) => {
    if (!value) return '';
    return value
      .replace(/\D/g, '')
      .slice(0, 14)
      .replace(/(\d{3})(?=\d)/g, '$1 ')
      .trim();
  }, []);
  useEffect(() => {
    setValue('siret', formatSiret(siret));
  }, [formatSiret, setValue, siret]);

  const { data: agencies } = useFetchAMAgencies({ enabled: isOpen });

  return (
    <Modal size="medium" open={isOpen} onClose={() => handleClose()}>
      <ModalContent
        title={`ajouter un routage ${selectedSource.toLowerCase()}`}
        header={<ActionNavy />}
      >
        <div className={styles.inputContainer}>
          <div className={styles.sectionTitle}>infos du mail</div>
          <div className={styles.inputRow}>
            <WithLightTitle title="qualification" className={styles.inputElement}>
              <TextInput name="qualification" control={control}></TextInput>
            </WithLightTitle>
            <WithLightTitle title="nom du client" className={styles.inputElement}>
              <TextInput name="clientName" control={control}></TextInput>
            </WithLightTitle>
          </div>
          <div className={styles.inputRow}>
            <WithLightTitle title="service" className={styles.inputElement}>
              <TextInput
                name="service"
                control={control}
                disabled={selectedSource === AppelMedicalEventSource.ARTEMIHS}
              ></TextInput>
            </WithLightTitle>
          </div>
          <div className={styles.sectionTitle}>infos osmose</div>
          <div className={styles.inputRow}>
            <WithLightTitle title="siret" className={styles.inputElement}>
              <TextInput
                name="siret"
                placeholder="ex: XXX XXX XXX XXX XX"
                control={control}
              ></TextInput>
            </WithLightTitle>
            <WithLightTitle title="code agence" className={styles.inputElement}>
              <SearchableDropDown
                items={agencies ?? []}
                selectedItem={watch('agencyId')}
                keyValueExtractor={item => {
                  return {
                    key: item,
                    label: item,
                    value: item,
                    item,
                  };
                }}
                onSelectItem={item => {
                  if (!item) return;
                  setValue('agencyId', item);
                }}
                placeholder="sélectionner une marque"
              />
            </WithLightTitle>
          </div>
        </div>
        <div className={styles.inputRow}>
          <div className={styles.inputRow}>
            <WithLightTitle title="marque" className={styles.inputElement}>
              <SearchableDropDown
                items={brandCodes}
                selectedItem={watch('brandCode')}
                keyValueExtractor={item => {
                  return {
                    key: item,
                    label: item,
                    value: item,
                    item,
                  };
                }}
                onSelectItem={item => {
                  if (!item) return;
                  setValue('brandCode', item);
                }}
                placeholder="sélectionner une marque"
              />
            </WithLightTitle>
          </div>
        </div>
      </ModalContent>
      <ModalActions side="left">
        <Button
          variant="tertiary"
          onClick={() => {
            reset();
          }}
        >
          réinitialiser
        </Button>
      </ModalActions>
      <ModalActions side="right">
        <Button
          variant="secondary"
          onClick={() => {
            reset({});
            setIsOpen(false);
          }}
        >
          annuler
        </Button>
        <Button
          variant="primary"
          type="submit"
          mutationStatus={createRoutingMutation.status}
          onClick={handleSubmit(data => {
            createRoutingMutation.mutate(
              {
                ...data,
                siret: data.siret?.replace(/\s+/g, ''),
                source: selectedSource,
              } as CreateRoutingInfoBody,
              {
                onSuccess: () => {
                  setTimeout(() => {
                    reset();
                    createRoutingMutation.reset();
                    setIsOpen(false);
                  }, 500);
                },
                onError: () => {
                  setTimeout(() => {
                    createRoutingMutation.reset();
                  }, 500);
                },
              }
            );
          })}
        >
          valider
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default AddRoutingModalComponent;
